/**
 * @fileoverview TanStack Query layer for rift query results and stat aggregates.
 *
 * Replaces the hand-rolled `datasetRifts$` Legend State cache. The lifecycle
 * concerns that store implemented by hand map onto Query built-ins:
 * - refcount eviction  -> observer-counted `gcTime`
 * - generation guard   -> query-key identity + the `queryFn` abort signal
 * - stale-while-revalidate -> `placeholderData: keepPreviousData` (in the hooks)
 *
 * Consumers hold a `riftId` (an FNV-1a hash of `runId + SQL`) but the `queryFn`
 * needs the SQL. The reactive id-deriving hooks (`use-rift.ts`) register a
 * lightweight {@link RiftDescriptor} (metadata only — no result payload) keyed
 * by `riftId`, and every query is rebuilt from that descriptor. Descriptors are
 * tiny and immutable per `riftId`, so the registry never holds large payloads.
 */

import type { DataType } from "@repo/lanes";
import type { LanesColumn } from "@repo/lanes/types";
import {
  type ColumnsConfig,
  type FilterType,
  type OperationType,
  parseStatsResult,
  previousPeriodStatsSql,
  type RiftConfig,
  statsSql,
} from "@repo/shared/rift";
import { queryOptions } from "@tanstack/react-query";

import { queryClient } from "~/libs/query-client";
import duckdb from "~/workers/duckdb";
import { ensureDatasetPayload } from "../datasets/datasets.actions";
import { ensureRunPayload } from "../datasets/rift-dataset-payload.query";
import { createRiftData, type RiftData } from "./rift-data";

const RIFT_QUERY_PREFIX = "rift";
const RIFT_STATS_QUERY_PREFIX = "rift-stats";

/** GC delay for zero-observer rift results. Replaces the count-based warmth budget. */
const RIFT_GC_MS = 5 * 60_000;

/**
 * Immutable metadata needed to (re)run a rift's queries. Registered by the
 * id-deriving hooks so any consumer holding only a `riftId` can build the
 * corresponding query options.
 *
 * `datasetId` is `null` for run-only rifts (e.g. a chat `runQuery` result):
 * the payload is ensured directly from the immutable run via `ensureRunPayload`
 * rather than through the dataset's current-run pointer. Everything downstream
 * (DuckDB query, `createRiftData`, stat batching) is already run-based.
 */
export type RiftDescriptor = {
  riftId: string;
  datasetId: string | null;
  runId: string;
  sql: string;
  riftConfig: RiftConfig | null;
  boardFilters: FilterType[] | null;
  columnsConfig: ColumnsConfig | null;
  columnDefs: Record<string, DataType> | null;
};

/**
 * Cache-key segment standing in for a null `datasetId` (run-only rifts). A real
 * dataset id can never equal this sentinel, so dataset-scoped cache maintenance
 * (`removeRiftQueriesForDataset`, `isDatasetRiftActive`) never matches run-only
 * entries — they are evicted purely by observer-counted `gcTime`.
 */
const RUN_ONLY_KEY_SEGMENT = "run" as const;

/**
 * Ensures the DuckDB-registered Arrow payload for a descriptor's run. Dataset
 * rifts go through `ensureDatasetPayload` (which also refreshes the dataset's
 * current-run pointer); run-only rifts ensure the immutable run directly.
 */
const ensurePayloadForDescriptor = async (
  descriptor: RiftDescriptor,
  signal?: AbortSignal,
): Promise<void> => {
  if (descriptor.datasetId) {
    await ensureDatasetPayload(descriptor.datasetId, {
      signal,
      runId: descriptor.runId,
    });
    return;
  }
  await ensureRunPayload({ runId: descriptor.runId, signal });
};

/** The resolved result of a main rift query. */
export type RiftQueryResult = {
  columns: Record<string, LanesColumn>;
  data: RiftData;
};

const riftDescriptors = new Map<string, RiftDescriptor>();

/** Registers (idempotently) the descriptor for a `riftId`. */
export const registerRiftDescriptor = (descriptor: RiftDescriptor): void => {
  riftDescriptors.set(descriptor.riftId, descriptor);
};

/** Reads a previously-registered descriptor. */
export const getRiftDescriptor = (
  riftId: string | null | undefined,
): RiftDescriptor | undefined =>
  riftId ? riftDescriptors.get(riftId) : undefined;

/** Stable query key for a main rift result. */
export const riftQueryKey = (descriptor: RiftDescriptor) =>
  [
    RIFT_QUERY_PREFIX,
    descriptor.datasetId ?? RUN_ONLY_KEY_SEGMENT,
    descriptor.runId,
    descriptor.riftId,
  ] as const;

/**
 * Query options for a main rift result. Registers nothing — pass a `riftId`
 * whose descriptor was already registered by an id-deriving hook. Disabled
 * (never fetches) when the descriptor is missing.
 */
export const riftQueryOptions = (riftId: string | null) => {
  const descriptor = getRiftDescriptor(riftId);
  return queryOptions({
    queryKey: descriptor
      ? riftQueryKey(descriptor)
      : ([RIFT_QUERY_PREFIX, "unregistered", riftId ?? "none"] as const),
    queryFn: async ({ signal }): Promise<RiftQueryResult> => {
      if (!descriptor) throw new Error("Rift descriptor not registered");
      await ensurePayloadForDescriptor(descriptor, signal);
      const result = await duckdb.queryTable(descriptor.sql);
      return { columns: result.columns, data: createRiftData(result.table) };
    },
    enabled: !!descriptor,
    // Results are immutable per (etag, sql), so never stale; released by gcTime
    // once no consumer observes them.
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: RIFT_GC_MS,
    // DuckDB errors are deterministic; the block-level "Retry" calls refetch.
    retry: false,
  });
};

// ---------------------------------------------------------------------------
// Stat aggregate batching
//
// The declarative `useRiftStat` hook runs one query per (column, operation,
// variant), which keeps caching granular but would otherwise emit one DuckDB
// scan per stat. DuckDB-WASM runs serially in a single worker, so a stat-heavy
// board would queue N full scans ahead of the chart/table queries. To restore
// the old single-scan-per-block behaviour we coalesce every stat `queryFn` for
// the same (riftId, variant) that lands within a microtask window into one
// `statsSql` / `previousPeriodStatsSql` query, then fan the parsed result back
// out to each caller. Query keys/caching stay per-stat.
// ---------------------------------------------------------------------------

type StatVariant = "current" | "previous";

/** One pending stat aggregate awaiting its coalesced batch result. */
type PendingStat = {
  column: string;
  operation: OperationType;
  resolve: (value: unknown) => void;
  reject: (err: unknown) => void;
};

type StatBatch = {
  descriptor: RiftDescriptor;
  variant: StatVariant;
  entries: PendingStat[];
  timer: ReturnType<typeof setTimeout> | null;
};

const statBatches = new Map<string, StatBatch>();

const statBatchKey = (riftId: string, variant: StatVariant): string =>
  `${riftId}::${variant}`;

/** Runs the coalesced stat query for a batch and resolves every waiter. */
const flushStatBatch = async (key: string): Promise<void> => {
  const batch = statBatches.get(key);
  if (!batch) return;
  statBatches.delete(key);

  const { descriptor, variant, entries } = batch;
  try {
    await ensurePayloadForDescriptor(descriptor);

    const statEntries = entries.map((e) => ({
      column: e.column,
      operation: e.operation,
    }));

    const sql =
      variant === "previous"
        ? previousPeriodStatsSql(
            `${descriptor.runId}.arrow`,
            descriptor.riftConfig,
            descriptor.boardFilters ?? [],
            statEntries,
            descriptor.columnDefs,
            descriptor.columnsConfig,
            { now: new Date() },
          )
        : statsSql(descriptor.sql, statEntries, descriptor.riftConfig);

    if (!sql) {
      for (const e of entries) e.resolve(null);
      return;
    }

    const result = await duckdb.queryRow(sql);
    const parsed = parseStatsResult(result.row ?? {});
    for (const e of entries) {
      e.resolve(parsed[e.column]?.[e.operation] ?? null);
    }
  } catch (err) {
    for (const e of entries) e.reject(err);
  }
};

/**
 * Enqueues a single stat aggregate into the shared (riftId, variant) batch,
 * scheduling a flush on first enqueue. Returns the value for this specific
 * (column, operation).
 */
const enqueueStat = (
  descriptor: RiftDescriptor,
  variant: StatVariant,
  column: string,
  operation: OperationType,
): Promise<unknown> => {
  const key = statBatchKey(descriptor.riftId, variant);
  let batch = statBatches.get(key);
  if (!batch) {
    batch = { descriptor, variant, entries: [], timer: null };
    statBatches.set(key, batch);
  }
  const currentBatch = batch;
  return new Promise<unknown>((resolve, reject) => {
    currentBatch.entries.push({ column, operation, resolve, reject });
    if (currentBatch.timer === null) {
      currentBatch.timer = setTimeout(() => {
        void flushStatBatch(key);
      }, 0);
    }
  });
};

/** Stable query key for a single stat aggregate. */
export const riftStatQueryKey = (
  descriptor: RiftDescriptor,
  column: string,
  operation: OperationType,
  variant: "current" | "previous",
) =>
  [
    RIFT_STATS_QUERY_PREFIX,
    descriptor.datasetId ?? RUN_ONLY_KEY_SEGMENT,
    descriptor.runId,
    descriptor.riftId,
    column,
    operation,
    variant,
  ] as const;

/**
 * Query options for a single stat aggregate (footer value / KPI / delta).
 * `variant: "previous"` computes the previous-period value for deltas.
 */
export const riftStatQueryOptions = (
  riftId: string | null,
  column: string,
  operation: OperationType | null | undefined,
  opts?: { previous?: boolean; enabled?: boolean },
) => {
  const descriptor = getRiftDescriptor(riftId);
  const variant = opts?.previous ? "previous" : "current";
  return queryOptions({
    queryKey:
      descriptor && operation
        ? riftStatQueryKey(descriptor, column, operation, variant)
        : ([
            RIFT_STATS_QUERY_PREFIX,
            "unregistered",
            riftId ?? "none",
            column,
            operation ?? "none",
            variant,
          ] as const),
    queryFn: async (): Promise<unknown> => {
      if (!descriptor || !operation) return null;
      // Coalesce with any other stat for the same (riftId, variant) so the
      // board issues one DuckDB scan per block instead of one per stat.
      return enqueueStat(descriptor, variant, column, operation);
    },
    enabled: !!descriptor && !!operation && opts?.enabled !== false,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: RIFT_GC_MS,
    retry: false,
  });
};

/** Reads the currently-cached result for a rift (non-reactive), if any. */
export const getRiftResult = (
  riftId: string | null,
): RiftQueryResult | undefined => {
  const descriptor = getRiftDescriptor(riftId);
  if (!descriptor) return undefined;
  return queryClient.getQueryData<RiftQueryResult>(riftQueryKey(descriptor));
};

/**
 * Ensures a rift result is fetched (running the query if needed) and returns
 * it. Used by non-React consumers such as the CSV export.
 */
export const ensureRiftResult = async (
  riftId: string | null,
): Promise<RiftQueryResult | undefined> => {
  if (!riftId) return undefined;
  return await queryClient.ensureQueryData(riftQueryOptions(riftId));
};

/**
 * True when at least one mounted consumer is observing a rift result for the
 * dataset. Replaces the old "is there an in-memory rift entry" displaying
 * check that read the deleted `datasetRifts$` store.
 */
export const isDatasetRiftActive = (datasetId: string): boolean => {
  const queries = queryClient.getQueryCache().findAll({
    predicate: (query) => {
      const key = query.queryKey;
      return (
        Array.isArray(key) &&
        key[0] === RIFT_QUERY_PREFIX &&
        key[1] === datasetId
      );
    },
  });
  return queries.some((query) => query.getObserversCount() > 0);
};

/**
 * Removes cached rift + stat query entries (and descriptors) for a dataset,
 * optionally preserving the newly-current run. Replaces `cleanupStaleRifts`.
 *
 * Stale-run work is always cancelled, but only entries with **no live
 * observer** are removed: a query with a mounted consumer is left alone so it
 * is garbage-collected naturally once that consumer re-derives the new run's
 * riftId. Force-removing an observed query would make TanStack Query
 * immediately refetch the doomed old-run query, adding load churn on every
 * republish. Descriptors for still-observed rifts are likewise retained.
 */
export const removeRiftQueriesForDataset = (
  datasetId: string,
  options?: { keepRunId?: string | null },
): void => {
  const keepRunId = options?.keepRunId ?? null;
  const matches = (key: unknown): boolean => {
    if (!Array.isArray(key)) return false;
    if (key[0] !== RIFT_QUERY_PREFIX && key[0] !== RIFT_STATS_QUERY_PREFIX)
      return false;
    if (key[1] !== datasetId) return false;
    if (keepRunId && key[2] === keepRunId) return false;
    return true;
  };

  void queryClient.cancelQueries({ predicate: (q) => matches(q.queryKey) });

  const cache = queryClient.getQueryCache();
  const staleQueries = cache.findAll({
    predicate: (q) => matches(q.queryKey),
  });

  // riftIds (query-key index 3, shared by rift + stat keys) still observed.
  const observedRiftIds = new Set<string>();
  for (const query of staleQueries) {
    if (query.getObserversCount() > 0) {
      const riftId = query.queryKey[3];
      if (typeof riftId === "string") observedRiftIds.add(riftId);
      continue;
    }
    cache.remove(query);
  }

  for (const [riftId, descriptor] of riftDescriptors) {
    if (descriptor.datasetId !== datasetId) continue;
    if (keepRunId && descriptor.runId === keepRunId) continue;
    if (observedRiftIds.has(riftId)) continue;
    riftDescriptors.delete(riftId);
  }
};

/** Test-only: clears the descriptor registry. */
export const __resetRiftDescriptorsForTests = (): void => {
  riftDescriptors.clear();
};
